import {Component, Input, OnInit} from '@angular/core';
import {AppContext} from "../../../../app-context";
import {MeterMetadataField, RefdataUtils, ResolvedMeterMetadata} from "../../refdata-utils";
import {
    Category,
    Meter,
    MeterDetails,
    MeterType,
    Tag,
    UpdateMeterDetails
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {SentenceCasePipe} from "../../../../common/sentence-case.pipe";
import {View} from "../../../../common/view";
import {Handler} from "../../../../common/handler";
import {Entity} from "../../../../handlers/entity";
import {cloneDeep} from "lodash";
import {trackByIndex} from "../../../../common/utils";
import {openConfirmationModalWithCallback} from '../../../../app-utils';
import {
    ModalConfirmAutofocus,
    ModalConfirmAutofocusData
} from '../../../../common/modal-confirm/modal-confirm.component';
import {TranslateDirective} from '../../../../common/utils/translate.directive';
import {
    calculatedResidualSourceMeterNumber,
    effectiveMeterType,
    hasActiveSourceBpm,
    hasActiveSourceChargePoint,
    isCalculatedResidual,
    isChargePointMeter,
    isMeterVisible
} from '../submeter-role.utils';

@Component({
    selector: 'app-meter-details',
    templateUrl: './meter-details.component.html',
    styleUrls: ['./meter-details.component.scss']
})
@Handler()
export class MeterDetailsComponent extends View implements OnInit {
    appContext = AppContext;
    refDataUtils = RefdataUtils;
    details: MeterDetails;
    @Input() data: MeterComponentData;
    meterTypes: MeterType[] = [MeterType.PRIMARY, MeterType.INTERMEDIATE, MeterType.GROSS_PRODUCTION];
    categories: Category[] = [Category.department, Category.custom];
    sourceMeterNumber: string;
    newTags: Tag[] = [];
    isPrimaryMeter = false;
    isResidualMeter = false;
    isCoolingMeter = false;
    isSourceBpm = false;
    isSourceChargePoint = false;
    visible = true;
    bpm = false;
    chargePoint = false;
    metadataFields: MeterMetadataFormField[] = [
        {field: 'tenant', label: 'Tenant'},
        {field: 'floor', label: 'Floor'},
        {field: 'tower', label: 'Tower'},
        {field: 'wing', label: 'Wing'}
    ];
    metadataForm: Record<MeterMetadataField, ResolvedMeterMetadata> = {} as Record<MeterMetadataField, ResolvedMeterMetadata>;
    sourceMetadataHelp = TranslateDirective.getTranslation(
        'managed by the source system and cannot be edited.', true) as string;

    protected readonly trackByIndex = trackByIndex;
    meterTypeFormatter = (v: string): string => SentenceCasePipe.format(v);

    ngOnInit(): void {
        const meter = this.data.entity.meter;
        this.isPrimaryMeter = effectiveMeterType(meter) === MeterType.PRIMARY;
        this.isResidualMeter = isCalculatedResidual(meter);
        this.isCoolingMeter = meter.info.type === MeterType.COOLING;
        this.isSourceBpm = meter.info.type === MeterType.GROSS_PRODUCTION;
        this.isSourceChargePoint = this.data.entity.connection.info.connectionType === 'ChargePoint';
        this.visible = isMeterVisible(meter);
        this.bpm = !this.isSourceChargePoint && effectiveMeterType(meter) === MeterType.GROSS_PRODUCTION;
        this.chargePoint = !this.isSourceBpm && isChargePointMeter(this.data.entity);
        this.sourceMeterNumber = this.isResidualMeter
            ? calculatedResidualSourceMeterNumber(this.data.entity.connection?.connectionId)
            : (meter.info.meterNumbers || []).filter(Boolean).join(', ') || meter.info.accessPointId;
        this.details = cloneDeep(meter.details) || {
            type: meter.info.type,
            label: meter.info.label,
            contractedCapacity: meter.info.contractedCapacity,
            meterNumbers: []
        };
        if (!this.details.meterNumbers || this.details.meterNumbers.length === 0) {
            this.details.meterNumbers = [''];
        }
        if (!this.details.tags) {
            this.details.tags = [];
        }
        this.metadataForm = this.createMetadataForm(meter);
    }

    onBpmChange(enabled: boolean): void {
        if (!enabled) {
            this.bpm = this.isSourceBpm;
            return;
        }
        const apply = () => {
            this.bpm = true;
            this.chargePoint = false;
        };
        if (!this.isSourceBpm && hasActiveSourceBpm(this.data.entity.location, this.data.entity.meter.meterId)) {
            openConfirmationModalWithCallback(result => {
                if (result === true) {
                    apply();
                    this.visible = true;
                } else if (result === 'hidden') {
                    apply();
                    this.visible = false;
                } else {
                    this.bpm = false;
                }
            }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
                type: 'warning',
                title: 'Existing production meter found',
                message: 'An active BPM is already linked. Choose how you want to process this meter.',
                cancelText: 'Cancel',
                alternativeText: 'Save hidden',
                alternativeValue: 'hidden',
                confirmText: 'Add both BPMs'
            }, 'static');
        } else {
            apply();
        }
    }

    onVisibleChange(enabled: boolean): void {
        const meterId = this.data.entity.meter.meterId;
        const conflictingBpm = this.bpm && !this.isSourceBpm
            && hasActiveSourceBpm(this.data.entity.location, meterId);
        const conflictingChargePoint = this.chargePoint && !this.isSourceChargePoint
            && hasActiveSourceChargePoint(this.data.entity.location, meterId);
        if (!enabled || (!conflictingBpm && !conflictingChargePoint)) {
            this.visible = enabled;
            return;
        }
        openConfirmationModalWithCallback(result => {
            if (result === true) {
                this.visible = true;
            } else if (result === 'intermediate') {
                this.visible = true;
                this.bpm = false;
                this.chargePoint = false;
            } else {
                this.visible = false;
            }
        }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
            type: 'warning',
            title: conflictingChargePoint ? 'Make charge point meter visible' : 'Make BPM visible',
            message: conflictingChargePoint
                ? 'Active charge point data is already linked. How do you want to make this meter visible?'
                : 'Another active BPM already exists. How do you want to make this meter visible?',
            cancelText: 'Cancel',
            alternativeText: 'Show as intermediate meter',
            alternativeValue: 'intermediate',
            confirmText: conflictingChargePoint ? 'Use both sources' : 'Add both BPMs'
        }, 'static');
    }

    onChargePointChange(enabled: boolean): void {
        if (!enabled) {
            this.chargePoint = this.isSourceChargePoint;
            return;
        }
        const apply = () => {
            this.chargePoint = true;
            this.bpm = false;
        };
        if (!this.isSourceChargePoint && hasActiveSourceChargePoint(this.data.entity.location, this.data.entity.meter.meterId)) {
            openConfirmationModalWithCallback(result => {
                if (result === true) {
                    apply();
                    this.visible = true;
                } else if (result === 'hidden') {
                    apply();
                    this.visible = false;
                } else {
                    this.chargePoint = false;
                }
            }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
                type: 'warning',
                title: 'Existing charge point data found',
                message: 'Active charge point data is already linked. Choose how you want to process this meter.',
                cancelText: 'Cancel',
                alternativeText: 'Save hidden',
                alternativeValue: 'hidden',
                confirmText: 'Use both sources'
            }, 'static');
        } else {
            apply();
        }
    }

    addTag() {
        if (this.newTags.length > 0) {
            return;
        }
        this.newTags.push({
            category: null,
            value: '',
            customType: ''
        });
    }

    removeTag(array: Tag[], index: number) {
        array.splice(index, 1);
    }

    isDetailCategoryTag(tag: Tag): boolean {
        return !this.getMetadataFieldForTag(tag);
    }

    onCategoryChange(tag: Tag) {
        if (tag.category !== Category.custom) {
            tag.customType = '';
        }
    }

    isTagValid(tag: Tag): boolean {
        return !!tag.category
            && !!tag.value?.trim()
            && (tag.category !== Category.custom || !!tag.customType?.trim());
    }

    confirmTag(tag: Tag, index: number) {
        if (!this.isTagValid(tag)) {
            return;
        }
        const metadataField = this.getMetadataFieldForTag(tag);
        if (metadataField) {
            if (!this.isSourceMetadataField(metadataField)) {
                this.metadataForm[metadataField].value = tag.value.trim();
                RefdataUtils.setMeterMetadataDetailTag(this.details, metadataField, tag.value);
            }
            this.removeTag(this.newTags, index);
            return;
        }
        this.details.tags.push({
            category: tag.category,
            value: tag.value.trim(),
            customType: tag.category === Category.custom ? tag.customType.trim() : ''
        });
        this.removeTag(this.newTags, index);
    }

    isSourceMetadataField(field: MeterMetadataField): boolean {
        return this.metadataForm[field]?.source === 'info';
    }

    updateInfo = () => {
        if (this.isCoolingMeter) {
            this.details.type = MeterType.COOLING;
            this.details.chargePoint = false;
            this.details.visible = this.visible;
        } else if (this.isPrimaryMeter) {
            this.details.type = MeterType.PRIMARY;
        } else {
            this.applyEditableMetadataTags();
            (this.details as any).visible = this.visible;
            (this.details as any).chargePoint = this.isResidualMeter ? false : this.chargePoint;
            this.details.type = this.isResidualMeter ? MeterType.INTERMEDIATE
                : this.bpm ? MeterType.GROSS_PRODUCTION : MeterType.INTERMEDIATE;
        }
        const command = this.isResidualMeter
            ? 'com.flowmaps.api.organisation.UpsertCalculatedResidualMeterDetails'
            : 'com.flowmaps.api.organisation.UpdateMeterDetails';
        this.sendCommand(command, <UpdateMeterDetails>{
            organisationId: this.data.entity.organisation.organisationId,
            locationId: this.data.entity.location.locationId,
            ...(this.isResidualMeter ? {} : {meterId: this.data.entity.meter.meterId}),
            connectionId: this.data.entity.connection.connectionId,
            details: this.details
        });
    };

    private createMetadataForm(meter: Meter): Record<MeterMetadataField, ResolvedMeterMetadata> {
        return this.metadataFields.reduce((form, metadataField) => {
            form[metadataField.field] = RefdataUtils.resolveMeterMetadata(meter, metadataField.field);
            return form;
        }, {} as Record<MeterMetadataField, ResolvedMeterMetadata>);
    }

    private applyEditableMetadataTags(): void {
        this.metadataFields
            .map(metadataField => metadataField.field)
            .filter(field => !this.isSourceMetadataField(field))
            .forEach(field => RefdataUtils.setMeterMetadataDetailTag(this.details, field, this.metadataForm[field]?.value));
    }

    private getMetadataFieldForTag(tag: Tag): MeterMetadataField | null {
        return this.metadataFields.find(metadataField =>
            RefdataUtils.isMeterMetadataTag(tag, metadataField.field))?.field || null;
    }
}

export interface MeterComponentData {
    entity: Entity;
}

interface MeterMetadataFormField {
    field: MeterMetadataField;
    label: string;
}
